import { createSlice, ListenerMiddlewareInstance, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../../configuration/setup/store';
import {
    TransportAssignment,
    TransportAssignmentPage,
    TransportAssignmentStatus,
} from '../transportAssignment/transportAssignment.types';
import { Filters, ModalFilters } from '../transportAssignment/filter.types';
import { TransportAssignmentTableColumn } from '../transportAssignment/table/TransportAssignmentTableColumns';
import { TableCategory } from '../transportAssignment/tableCategory.types';
import { SortDirection, TableSort } from '../sharedComponents/table/SortedTableHeader';
import { difference, union } from 'es-toolkit/array';
import { matchAssignmentsReceived } from '../api/transportOrderApi';
import { WritableDraft } from 'immer';
import { createDebouncedValue } from '../../../configuration/setup/debouncedStateUpdate';
import { DEBOUNCE_TIME } from '../debounceConfig';

export const NO_SELECTED_TRANSPORT_ASSIGNMENT = undefined;

export type Sorting = { [key in TableCategory]: TableSort<TransportAssignmentTableColumn> };

export enum TransportAssignmentSearchMode {
    VIN = 'vin',
    MEANS_OF_TRANSPORT_ID = 'meansOfTransportId',
    BUNDLE_ID = 'bundleId',
    EXTERNAL_ORDER_ID = 'externalOrderId',
}

export type TransportAssignmentsState = {
    selectedTransportAssignment: TransportAssignment | undefined;
    filters: Filters;
    sorting: Sorting;
    searchMode: TransportAssignmentSearchMode;
    search: string;
    debouncedSearch: string;
    bulkSelection: Record<TableCategory, string[]>;
};

export const initialFilterState = {
    modalFilters: {
        startDateFilter: undefined,
        endDateFilter: undefined,
        pickUpLocation: undefined,
        deliveryLocation: undefined,
        meansOfTransportTypeFilter: undefined,
        vehicleTypeFilter: undefined,
        manufacturerFilter: undefined,
    },
    statusFilter: [],
    category: TableCategory.INBOX,
};

export const initialSortingState: Sorting = {
    [TableCategory.INBOX]: { dir: SortDirection.DESCENDING, column: TransportAssignmentTableColumn.REQUESTED_PICKUP },
    [TableCategory.EN_ROUTE]: {
        dir: SortDirection.ASCENDING,
        column: TransportAssignmentTableColumn.REQUESTED_DELIVERY,
    },
    [TableCategory.FINISHED]: {
        dir: SortDirection.ASCENDING,
        column: TransportAssignmentTableColumn.REQUESTED_DELIVERY,
    },
    [TableCategory.ARCHIVE]: {
        dir: SortDirection.ASCENDING,
        column: TransportAssignmentTableColumn.REQUESTED_DELIVERY,
    },
};

export const initialTransportAssignmentsState: TransportAssignmentsState = {
    selectedTransportAssignment: NO_SELECTED_TRANSPORT_ASSIGNMENT,
    filters: initialFilterState,
    sorting: initialSortingState,
    searchMode: TransportAssignmentSearchMode.VIN,
    search: '',
    debouncedSearch: '',
    bulkSelection: {
        [TableCategory.INBOX]: [],
        [TableCategory.EN_ROUTE]: [],
        [TableCategory.FINISHED]: [],
        [TableCategory.ARCHIVE]: [],
    },
};

const debouncedSearchSetup = createDebouncedValue<string>({
    typePrefix: 'transportAssignments/search',
    debounceTime: DEBOUNCE_TIME,
});

export const transportAssignmentsSlice = createSlice({
    name: 'transportAssignments',
    initialState: initialTransportAssignmentsState,
    reducers: {
        selectTransportAssignment: (state: TransportAssignmentsState, action: PayloadAction<TransportAssignment>) => {
            state.selectedTransportAssignment = action.payload;
        },
        deselectTransportAssignment: (state: TransportAssignmentsState) => {
            state.selectedTransportAssignment = NO_SELECTED_TRANSPORT_ASSIGNMENT;
        },
        setModalFilters: (state: TransportAssignmentsState, action: PayloadAction<ModalFilters>) => {
            state.filters.modalFilters = action.payload;
        },
        setStatusFilter: (state: TransportAssignmentsState, action: PayloadAction<TransportAssignmentStatus[]>) => {
            state.filters.statusFilter = action.payload;
        },
        setCategory: (state: TransportAssignmentsState, action: PayloadAction<TableCategory>) => {
            state.filters.category = action.payload;
        },
        setSortingForCategory: (
            state: TransportAssignmentsState,
            action: PayloadAction<{ category: TableCategory; sort: TableSort<TransportAssignmentTableColumn> }>,
        ) => {
            state.sorting[action.payload.category] = action.payload.sort;
        },
        setSearchMode: (state: TransportAssignmentsState, action: PayloadAction<TransportAssignmentSearchMode>) => {
            state.searchMode = action.payload;
        },
        setInitialState: () => initialTransportAssignmentsState,
        addToBulkSelection: (state: TransportAssignmentsState, action: PayloadAction<string[]>) => {
            state.bulkSelection[state.filters.category] = union(
                state.bulkSelection[state.filters.category],
                action.payload,
            );
        },
        removeFromBulkSelection: (state: TransportAssignmentsState, action: PayloadAction<string[]>) => {
            state.bulkSelection[state.filters.category] = difference(
                state.bulkSelection[state.filters.category],
                action.payload,
            );
        },
        clearBulkSelection: (state: TransportAssignmentsState) => {
            state.bulkSelection[state.filters.category] = [];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(debouncedSearchSetup.setValueAction, (state, action) => {
            state.search = action.payload.value;
        });

        builder.addCase(debouncedSearchSetup.setDebouncedValueAction, (state, action) => {
            state.debouncedSearch = action.payload;
        });

        builder.addMatcher(matchAssignmentsReceived, (state, action) => {
            resetSelectedAssignment(state, action);
        });
    },
});

const resetSelectedAssignment = (
    state: WritableDraft<TransportAssignmentsState>,
    action: PayloadAction<TransportAssignmentPage>,
) => {
    const assignments = action.payload.items;
    const selectedAssignmentId = state.selectedTransportAssignment?.id;
    if (
        selectedAssignmentId !== undefined &&
        !assignments.find((assignment) => assignment.id === selectedAssignmentId)
    ) {
        state.selectedTransportAssignment = NO_SELECTED_TRANSPORT_ASSIGNMENT;
    }
};

const getTransportAssignmentState = (state: RootState) => state.portal.transportAssignments;

export const getSelectedTransportAssignment = (state: RootState) =>
    getTransportAssignmentState(state).selectedTransportAssignment;

export const getFilters = (state: RootState) => getTransportAssignmentState(state).filters;
export const getModalFilters = (state: RootState) => getTransportAssignmentState(state).filters.modalFilters;
export const getStatusFilter = (state: RootState) => getTransportAssignmentState(state).filters.statusFilter;
export const getSelectedCategory = (state: RootState) => getTransportAssignmentState(state).filters.category;
export const getSortingForCategory = (category: TableCategory) => (state: RootState) =>
    getTransportAssignmentState(state).sorting[category];
export const getTransportAssignmentSearchMode = (state: RootState) => getTransportAssignmentState(state).searchMode;
export const getTransportAssignmentSearchTerm = (state: RootState) => getTransportAssignmentState(state).search;
export const getDebouncedTransportAssignmentSearchTerm = (state: RootState) =>
    getTransportAssignmentState(state).debouncedSearch;

export const getBulkSelection = (state: RootState) =>
    getTransportAssignmentState(state).bulkSelection[getTransportAssignmentState(state).filters.category];

export const setTransportAssignmentSearchTerm = debouncedSearchSetup.setValueAction;

export const addSliceListeners = (listenerMiddleware: ListenerMiddlewareInstance) => {
    listenerMiddleware.startListening(debouncedSearchSetup.listener);
};
